"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _getPropSelector = _interopRequireDefault(require("./getPropSelector"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
// This file contains common props for components that render a React Native TextInput
// It excludes interaction handler functions which are in `./handlerProps.js`

/**
 * TextInput (web and native) supports some common React Native props
 * shared with React Native's Text component.
 */
const textProps = {
  maxFontSizeMultiplier: _propTypes.default.number,
  nativeId: _propTypes.default.string,
  onLayout: _propTypes.default.func
};

/**
 * UDS text inputs accept props related to UDS's useInputValue hook
 */
const inputValueProps = {
  value: _propTypes.default.string,
  type: _propTypes.default.string,
  initialValue: _propTypes.default.string,
  readOnly: _propTypes.default.bool,
  inactive: _propTypes.default.bool
};

/**
 * This collection adds props that can be passed through to both React Native's
 * and React Native Web's implementations of the React Native TextInput component.
 */
const crossPlatform = {
  ...textProps,
  ...inputValueProps,
  /**
   * Web and Android; 'off' disables device autocomplete, other strings are platform-specific.
   * Valid values on Native: https://reactnative.dev/docs/textinput#autocomplete-android
   * Valid values on Web: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete: _propTypes.default.string,
  /**
   * On Native, default is `true`, passing `false` disables autoCorrect.
   * On web, only supported by Safari and expects "on" or "off" strings.
   */
  autoCorrect: _propTypes.default.oneOf([true, false, 'on', 'off']),
  /**
   * Focuses the element on mount. On Web, only the first form element with autoFocus is focussed.
   */
  autoFocus: _propTypes.default.bool,
  /**
   * Default is `true` for single line, `false` for multi-line
   */
  blurOnSubmit: _propTypes.default.bool,
  /**
   * iOS and Web only, no effect on Android
   */
  clearTextOnFocus: _propTypes.default.bool,
  /**
   * Default is `true`. On web, this works by mapping to input's `readonly` attribute
   */
  editable: _propTypes.default.bool,
  /**
   * See documentation for allowed values (varies between Web, Android and iOS) and important notes:
   * - Native: https://reactnative.dev/docs/textinput#keyboardtype
   * - Web: equivalent to `inputmode` but see https://necolas.github.io/react-native-web/docs/text-input/
   */
  keyboardType: _propTypes.default.string,
  /**
   * Uses native tools (no flicker) to cap the maximum number of characters.
   * On Web, works via `maxlength` attr.
   */
  maxLength: _propTypes.default.number,
  /**
   * If passed as true, the text input can be multiple lines.
   *
   * > It is important to note that this aligns the text to the top on iOS, and centers it on Android.
   * > Use with textAlignVertical set to top for the same behavior in both platforms.
   */
  multiline: _propTypes.default.bool,
  /**
   * Web and Android only, requires `multiline` to be `true`.
   */
  numberOfLines: _propTypes.default.number,
  /**
   * Text to display when no value set.
   * Accesibility guidelines recommend using labels to describe the input and using
   * placeholders rarely and sparingly to prompt a particular format.
   */
  placeholder: _propTypes.default.string,
  /**
   * Sets placeholder colour. On Web, uses `::placeholder { color: ... }` selector.
   */
  placeholderTextColor: _propTypes.default.string,
  /**
   * One of a subset of platform-specific options that controls labelling and presentation
   * in on-screen keyboards and accessibility tools. Uses `enterkeyhint` attr on Web.
   *
   * 'done', 'go', 'next', 'search', and 'send' are available for Web, Android and iOS.
   */
  returnKeyType: _propTypes.default.string,
  /**
   * Obscures passwords and similar. Equivalent to type="password" on Web.
   * Does not work if multiline is true.
   */
  secureTextEntry: _propTypes.default.bool,
  /**
   * If true, all text will automatically be selected on focus.
   */
  selectTextOnFocus: _propTypes.default.bool,
  /**
   * Web and iOS. On iOS, default inherits from `autoCorrect`.
   * On Web, equivalent to `spellcheck` attr.
   */
  spellCheck: _propTypes.default.bool
};

/**
 * These web-only props all control HTML `input` attributes of the same name.
 * Refer to general HTML documentation for more details.
 */
const webOnly = {
  disabled: _propTypes.default.bool,
  dir: _propTypes.default.oneOf(['auto', 'ltr', 'rtl']),
  lang: _propTypes.default.string,
  /**
   * Sets the HTML input `pattern` attr. Not supported by React Native Web, but is supported by UDS.
   * Must also pass in a ref and check validity by calling the HTML element's checkValidity method:
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/checkValidity
   */
  pattern: _propTypes.default.string
};

/**
 * These props are supported in React Native but not React Native Web.
 *
 * React Native text inputs can be quirky, so a full set of props should be allowed to handle edge cases.
 * Refer to React Native documentation for details, allowed values, and Android/iOS support and versions:
 * https://reactnative.dev/docs/textinput
 *
 * Beware that many React Native text input props apply via complicated logic that chooses a built-in
 * native component based on the values of multiple boolean flags, and may sometimes appear to pick an
 * option that is inappropriate for one flag based on the values of one or more other other flags.
 */
const nativeOnly = {
  caretHidden: _propTypes.default.bool,
  clearButtonMode: _propTypes.default.string,
  contextMenuHidden: _propTypes.default.bool,
  dataDetectorTypes: _propTypes.default.string,
  disableFullscreenUI: _propTypes.default.bool,
  enablesReturnKeyAutomatically: _propTypes.default.bool,
  importantForAutofill: _propTypes.default.string,
  inlineImageLeft: _propTypes.default.string,
  keyboardAppearance: _propTypes.default.string,
  returnKeyLabel: _propTypes.default.string,
  rejectResponderTermination: _propTypes.default.bool,
  scrollEnabled: _propTypes.default.bool,
  selection: _propTypes.default.object,
  selectionColor: _propTypes.default.string,
  showSoftInputOnFocus: _propTypes.default.bool,
  textAlign: _propTypes.default.string,
  textContentType: _propTypes.default.string,
  passwordRules: _propTypes.default.string,
  textBreakStrategy: _propTypes.default.string,
  underlineColorAndroid: _propTypes.default.string
};
var _default = exports.default = {
  /**
   * Subset of proptypes that can be passed down to a React Native or React Native Web
   * `TextInput` component. Allow regardless of platform, so cross-platform apps don't warn.
   */
  types: {
    ...crossPlatform,
    ...webOnly,
    ...nativeOnly
  },
  /**
   * Filters a props object. Return only platform-appropriate TextInput props, native inputs
   * may throw errors on receiving unknown props.
   */
  select: (0, _getPropSelector.default)({
    ...crossPlatform,
    ..._Platform.default.select({
      web: webOnly,
      default: nativeOnly
    })
  })
};